import React, { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Free address autocomplete powered by OpenStreetMap Nominatim.
 * No API key required. Debounced, keyboard-free selection via click.
 */
export const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder,
  testid,
  className = "",
  lang = "it",
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const skipNextFetch = useRef(false);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchSuggestions = useCallback(
    async (q) => {
      if (!q || q.trim().length < 3) {
        setSuggestions([]);
        setOpen(false);
        return;
      }
      setLoading(true);
      try {
        const url =
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6` +
          `&accept-language=${lang}&countrycodes=it&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [lang]
  );

  const handleInput = (e) => {
    const v = e.target.value;
    onChange(v);
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 400);
  };

  const pick = (s) => {
    skipNextFetch.current = true;
    onChange(s.display_name);
    onSelect?.({ label: s.display_name, lat: s.lat, lon: s.lon });
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <MapPin size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
        <input
          data-testid={testid}
          value={value}
          onChange={handleInput}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          className={`w-full pl-9 pr-9 ${className}`}
        />
        {loading && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-white/40" />}
      </div>

      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto border border-white/10 bg-matte-surface shadow-[0_10px_40px_rgba(0,0,0,0.5)]"
        >
          {suggestions.map((s) => (
            <li key={s.place_id}>
              <button
                type="button"
                onClick={() => pick(s)}
                className="flex w-full items-start gap-2 px-4 py-3 text-left font-body text-sm text-white/80 transition-colors hover:bg-gold hover:text-black"
              >
                <MapPin size={14} className="mt-0.5 shrink-0" />
                <span className="line-clamp-2">{s.display_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
